import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { XAxis, YAxis, Tooltip, LabelList, CartesianGrid, Legend, LineChart, Line, ResponsiveContainer } from "recharts";
import { Tooltip as TooltipEllips } from "src/assets/imports/antdComponents";
import { ChartGridType, chartType, ChartViewType, IDynamicChartsProps } from "src/pages/Insights/models/interfaces";
import { chartStyleConfigLine, formatValue, getTextWidth, renderNValueLabel } from "src/pages/Insights/transformer";
import { CustomMarker } from "src/components/Charts/DynamicCharts/components/CustomMarker";
import { CustomTooltip } from "src/components/Charts/DynamicCharts/components/CustomTooltip";
import AxisLabel from "../../../BarChart/components/AxisLable";
import { QuantComponentsRenderedFromType } from "src/pages/NewAskKural/models/interfaces";
import useAsyncChartConfig from "src/components/Hooks/useAsyncChartConfig";

export const LineHorizontal: React.FC<IDynamicChartsProps> = ({
	obj = {},
	type,
	config,
	isMini,
	legends = [],
	showYAxisLabel = true,
	showXAxis = true,
	maxDomain = "auto",
	minDomain = "auto",
	renderedFrom = QuantComponentsRenderedFromType.Quant,
	isSectionalSummary = false,
	chartSegmentLength = 1,
	viewType = ChartViewType.Dashboard,
	grid = ChartGridType.oneXone,
}) => {
	if (!obj) return null;
	const [hoveredLineKey, setHoveredLineKey] = useState<string | null>(null);
	const { n_size_value = [] } = obj;
	const [isTooltipHovered, setIsTooltipHovered] = useState(false);
	const bottomMargin = obj?.xAxisLables ? (obj?.graph_customization?.legend_pos !== "Top" ? 10 : 50) : obj?.graph_customization?.legend_pos !== "Top" ? 50 : 50;
	const data =
		legends?.length > 0
			? obj?.chartData?.map((item) => {
				return Object.fromEntries(Object.entries(item).filter(([key]) => !legends.includes(key)));
			})
			: obj?.chartData;
	// const barKeys = Object.keys(data?.[0] || {}).filter((key) => key !== config.dataKeyXAxis);
	const barKeys: string[] = data ? [...new Set((data as { [key: string]: any }[]).flatMap((obj) => Object.keys(obj).filter((key) => key !== config.dataKeyXAxis)))] : [];

	const { config: customConfig } = useAsyncChartConfig(obj?.chartType, viewType, grid);

	function getBottomPosition() {
		const legendPosition = obj?.graph_customization?.legend_pos;
		if (isSectionalSummary) return 0;
		if (legendPosition === "Top") {
			if (barKeys?.length === 1 && config.layout === "vertical") return 10;
			return obj?.xAxisLables ? 80 : 60;
		}
		if (config.layout !== "vertical") return 15;
		if (barKeys?.length === 1 && config.layout === "vertical") return 10;
		return 80 - bottomMargin;
	}

	const CustomXAxisTickBarTop2 = (props) => {
		const { x, y, payload } = props;
		const text = payload.value;

		const fontSize = customConfig?.x_axis_top?.tick?.font_size;
		const textWidth = getTextWidth(text, fontSize);
		const padding = customConfig?.x_axis_top?.tick?.padding; // total padding (px-2 = 0.5rem each side = 16px)
		const maxBoxWidth = customConfig?.x_axis_top?.tick?.max_width;
		const totalWidth = Math.min(textWidth + padding, maxBoxWidth) + 10;

		// Ellipsize if text is wider than the box
		const displayText =
			textWidth + padding > maxBoxWidth
				? (() => {
					let truncated = text;
					while (getTextWidth(truncated + "...", fontSize) + padding > maxBoxWidth && truncated.length > 0) {
						truncated = truncated.slice(0, -1);
					}
					return truncated.length < text.length ? truncated + "..." : text;
				})()
				: text;

		return (
			<g transform={`translate(${x},${y})`}>
				<foreignObject x={-totalWidth / 2} y={type?.toLowerCase() !== chartType.Line_Horizontal?.toLocaleLowerCase() ? -50 : -50} width={totalWidth} height={38}>
					<TooltipEllips title={text} overlayClassName="quant-tooltip-reduced-padding">
						<div className="flex justify-center items-center h-full w-full">
							<span
								className={`flex justify-center items-center text-[#5578b0] bg-[#f9fbfd] ${
									// type?.toLowerCase() !== chartType.Line_Horizontal?.toLocaleLowerCase() ? "border border-[rgba(94,128,183,0.15)] rounded-full  px-5 py-1" : ""
									"border border-[rgba(94,128,183,0.15)] rounded-full  px-5 py-1"
									} `}
								style={{
									fontSize,
									width: `${totalWidth}px`,
									whiteSpace: "nowrap",
									color: customConfig?.x_axis_top?.tick?.label_color,
									fontWeight: customConfig?.x_axis_top?.tick?.font_weight,
								}}>
								{displayText}
							</span>
						</div>
					</TooltipEllips>
				</foreignObject>
			</g>
		);
	};

	const renderColoredLabel = (labelParts, maxChars, shouldRotate, chartSegmentLength) => {
		let fullLabelPartsText =
			chartSegmentLength >= 1 && shouldRotate
				? labelParts
					?.map((part) => part.text)
					.join("")
					.replace(/\s+/g, "") || ""
				: labelParts?.map((part) => part.text).join("") || "";
		fullLabelPartsText = "(" + fullLabelPartsText + ")";
		let truncatedText = fullLabelPartsText;

		if (shouldRotate && fullLabelPartsText.length > maxChars && chartSegmentLength >= 1) {
			truncatedText = fullLabelPartsText.slice(0, maxChars - 3) + "...";
		}

		let currentLength = 0;
		let result = [];
		for (let i = 0; i < labelParts.length; i++) {
			const { text, color } = labelParts[i];
			let updatedText = text;

			if (i === 0) updatedText = "(" + text;
			if (i === labelParts.length - 1) updatedText = text + ")";
			if (currentLength >= truncatedText.length) break;

			// Remove spaces from part.text if chartSegmentLength > 1 and shouldRotate
			const processedText = chartSegmentLength >= 1 && shouldRotate ? updatedText.replace(/\s+/g, "") : updatedText;

			const remaining = truncatedText.length - currentLength;
			const visibleText = processedText.slice(0, remaining);

			if (visibleText) {
				result.push(
					<span key={i} style={{ color }}>
						{visibleText}
					</span>
				);
				currentLength += visibleText.length;
			}
		}

		// Append ellipsis if truncation occurred
		if (truncatedText.endsWith("...") && !fullLabelPartsText.endsWith("...")) {
			result.push(<span key="ellipsis">...</span>);
		}

		return result;
	};

	const CustomXAxisTickBottomLine = (props, i) => {
		const { x, y, payload, index } = props;
		const parsed = renderNValueLabel(n_size_value?.[index]);

		const barCount = props?.barCount || 0;
		// Determine if we should rotate based on chartSegmentLength and barCount
		const maxChars = customConfig?.x_axis_bottom?.tick?.max_char;
		const shouldRotate = isSectionalSummary ? barCount > 4 : (chartSegmentLength === 1 && barCount > 5) || (chartSegmentLength > 1 && barCount >= 3);
		const nSizeText = parsed?.labelParts?.map((part) => part.text).join("") || "";

		const isNSizeHidden = customConfig?.x_axis_bottom?.tick?.hide_n_size || false;
		const isNSizeWrapped = customConfig?.x_axis_bottom?.tick?.wrap_n_size ?? true;
		const text = shouldRotate ? (isNSizeHidden ? payload.value : isNSizeWrapped ? payload.value : payload.value + " (" + nSizeText + ")") : payload.value;

		// Updated tooltipText to match bar chart format
		const tooltipText = (
			<span>
				{payload.value} (
				{parsed?.labelParts?.map((part, idx) => (
					<span key={idx} style={{ color: part?.color }}>
						{part.text}
					</span>
				))}
				)
			</span>
		);

		const fontSize = customConfig?.x_axis_bottom?.tick?.font_size;
		const textWidth = getTextWidth(text, fontSize);
		const padding = customConfig?.x_axis_bottom?.tick?.padding; // total padding (px-2 = 0.5rem each side = 16px)
		const maxBoxWidth = customConfig?.x_axis_bottom?.tick?.max_width;

		// Calculate width for the text container
		const totalWidth = Math.min(textWidth + padding, maxBoxWidth) + 30;

		// For rotated text, we need to ensure enough width and height
		const rotatedWidth = totalWidth;
		const rotatedHeight = customConfig?.x_axis_bottom?.tick?.rotated_height;

		// Check if n-size label would be truncated
		const nSizeWidth = getTextWidth(`(${nSizeText})`, fontSize);
		const isNSizeTruncated = shouldRotate ? nSizeText.length > maxChars : nSizeWidth + padding > maxBoxWidth;

		// Ellipsize logic - different for rotated vs non-rotated text
		let isTextTruncated = false;
		const displayText = (() => {
			if (shouldRotate) {
				// For rotated text, limit to 5-6 characters max
				if (text.length > maxChars) {
					isTextTruncated = true;
					return text.slice(0, maxChars).trim() + "...";
				} else {
					return text;
				}
			} else {
				// Original logic for non-rotated text
				if (textWidth + padding > maxBoxWidth) {
					let truncated = text;
					while (getTextWidth(truncated + "...", fontSize) + padding > maxBoxWidth && truncated.length > 0) {
						truncated = truncated.slice(0, -1);
					}
					if (truncated.length < text.length) {
						isTextTruncated = true;
						return truncated.length < text.length ? truncated + "..." : text;
					} else {
						return text;
					}
				} else {
					return text;
				}
			}
		})();

		// Show tooltip if either x-axis text or n-size is truncated
		const showToolTip = isTextTruncated || isNSizeTruncated;

		const labelParts = parsed?.labelParts;

		const calculateTransformCoordinates = () => {
			// Base calculations
			const fontSizeMultiplier = fontSize / 12; // Normalize to base font size of 12
			const effectiveTextLength = displayText.length + 3;
			const originalTextLength = text.length;

			// Better logic for minor differences
			const textDifference = originalTextLength - effectiveTextLength;
			const isMinorTruncation = textDifference <= 4; // Minor difference threshold

			if (shouldRotate) {
				let adjustedX, adjustedY;

				if (isNSizeHidden) {
					// For hidden n-size scenario
					adjustedX = x - fontSize * 1.25 - effectiveTextLength * fontSize * 0.02;

					// Y coordinate adjustment - different logic for minor vs major differences
					const baseY = y + fontSize * 1.3 + fontSizeMultiplier * 2;

					let textLengthYOffset;
					if (isMinorTruncation) {
						// For minor differences, use original text length for positioning
						textLengthYOffset = (originalTextLength + 5) * fontSize * 0.06; // Reduced factor
					} else {
						// For major differences, use display text length
						textLengthYOffset = (effectiveTextLength - 5) * fontSize * 0.08;
					}

					adjustedY = baseY + textLengthYOffset;
				} else {
					// Standard rotation scenario
					adjustedX = isNSizeWrapped ? x - 15 : x - fontSize * 0.4 - effectiveTextLength * fontSize * 0.03;

					// Y coordinate adjustment with better logic
					const baseY = y - fontSize * 0.5 + fontSizeMultiplier * 1.5;

					let textLengthYOffset;
					if (isMinorTruncation) {
						// Use original text length for minor differences
						textLengthYOffset = (originalTextLength - 3) * fontSize * 0.04; // Reduced factor
						// Add small compensation for ellipsis
						const ellipsisCompensation = isTextTruncated ? fontSize * 0.15 : 0;
						textLengthYOffset += ellipsisCompensation;
					} else {
						// Use display text length for major differences
						textLengthYOffset = (effectiveTextLength - 3) * fontSize * 0.06;
					}

					adjustedY = baseY + textLengthYOffset;
				}

				// X adjustment with better logic for minor differences
				let textLengthXOffset;
				if (isMinorTruncation) {
					// For minor differences, base on original text length
					textLengthXOffset = (originalTextLength / Math.max(maxChars, 1)) * fontSize * 0.25;
				} else {
					// For major differences, base on display text length
					textLengthXOffset = (effectiveTextLength / Math.max(maxChars, 1)) * fontSize * 0.3;
				}
				adjustedX -= textLengthXOffset;

				// Fine-tune Y position to avoid axis line overlap
				const axisLineAvoidanceOffset = shouldRotate ? fontSize * 0.2 : 0;
				adjustedY += axisLineAvoidanceOffset;

				return { x: adjustedX, y: adjustedY };
			} else {
				// For non-rotated text
				const adjustedX = x;
				const adjustedY = y - 5;

				return { x: adjustedX, y: adjustedY };
			}
		};

		const { x: transformX, y: transformY } = calculateTransformCoordinates();

		const transformValue = shouldRotate
			? `translate(${transformX},${isNSizeWrapped ? transformY + 12 : chartSegmentLength > 2 && barCount > 6 ? transformY + 15 : transformY}) rotate(${customConfig?.x_axis_bottom?.tick?.angel
			})`
			: `translate(${transformX},${transformY})`;

		return (
			<g transform={transformValue}>
				<foreignObject
					// Position adjustments to ensure proper centering with the bar
					x={shouldRotate ? -(rotatedWidth / 3.5) : -totalWidth / 2}
					y={shouldRotate ? 5 : 5}
					width={shouldRotate ? rotatedWidth + 20 : totalWidth + 10}
					height={shouldRotate ? rotatedHeight : 38}>
					{showToolTip ? (
						<TooltipEllips placement={"bottom"} title={tooltipText} overlayClassName="quant-tooltip-reduced-padding">
							<div
								className="flex flex-col h-full w-full font-semibold"
								style={
									shouldRotate
										? {
											// transform: `rotate(${customConfig?.x_axis_bottom?.tick?.angel}deg)`,
											// transformOrigin: "20% 10%", // Changed to better align with bar
											// paddingTop: "5px",
											// marginLeft: "-3px",
										}
										: {
											display: "flex",
											flexDirection: "column",
											justifyContent: "center",
											alignItems: "center",
										}
								}>
								<span
									className={`text-[${customConfig?.x_axis_bottom?.tick?.label_color}]`}
									style={{
										fontSize,
										whiteSpace: "nowrap",
										display: "block", // Ensures text doesn't get cut off
										textAlign: shouldRotate ? "left" : "center",
										width: shouldRotate ? "auto" : `${totalWidth}px`,
									}}>
									{displayText}
								</span>
								{!isNSizeHidden && isNSizeWrapped && (
									<div>
										<span
											className="items-center"
											style={{
												color: customConfig?.x_axis_bottom?.tick?.label_color,
												fontSize: fontSize,
												display: "block", // Ensures text doesn't get cut off
												marginTop: shouldRotate ? "-2px" : undefined,
												marginLeft: shouldRotate ? (isNSizeWrapped ? "4px" : "7px") : undefined,
											}}>
											{renderColoredLabel(labelParts, maxChars, shouldRotate, chartSegmentLength)}
										</span>
									</div>
								)}
							</div>
						</TooltipEllips>
					) : (
						<div
							className="flex flex-col h-full w-full font-semibold"
							style={
								shouldRotate
									? {
										// transform: `rotate(${customConfig?.x_axis_bottom?.tick?.angel}deg)`,
										// transformOrigin: "30% 24%", // Changed to better align with bar
										// paddingTop: "5px",
										// marginLeft: "-5px",
									}
									: {
										display: "flex",
										flexDirection: "column",
										justifyContent: "center",
										alignItems: "center",
									}
							}>
							<span
								className={`text-[${customConfig?.x_axis_bottom?.tick?.label_color}]`}
								style={{
									fontSize,
									whiteSpace: "nowrap",
									display: "block", // Ensures text doesn't get cut off
									textAlign: shouldRotate ? "left" : "center",
									width: shouldRotate ? "auto" : `${totalWidth}px`,
									marginLeft: shouldRotate ? "4px" : undefined,
								}}>
								{displayText}
							</span>
							{!isNSizeHidden && isNSizeWrapped && (
								<div>
									<span
										className="items-center"
										style={{
											color: customConfig?.x_axis_bottom?.tick?.label_color,
											fontSize: fontSize,
											display: "block", // Ensures text doesn't get cut off
											marginTop: shouldRotate ? "-2px" : undefined,
											marginLeft: shouldRotate ? "4px" : undefined,
										}}>
										{renderColoredLabel(labelParts, maxChars, shouldRotate, chartSegmentLength)}
									</span>
								</div>
							)}
						</div>
					)}
				</foreignObject>
			</g>
		);
	};

	const CustomYAxisTick = (props) => {
		let showToolTip = true;
		const { x, y, payload } = props;
		const { i18n } = useTranslation(); // access current language/locale
		const maxWidth = customConfig?.y_axis_left?.tick?.max_width;
		const originalValue = payload.value;
		// const format = (obj.graph_customization?.dlabel_fmt || "##").replace(/\.\#+/, "");
		const format = obj?.graph_customization?.dlabel_fmt || "##";
		const value = formatValue(payload.value, format);

		const getEllipsisText = (str, maxLength) => {
			// if (isNaN(payload?.value)) {
			const canvas = document.createElement("canvas");
			const context = canvas.getContext("2d");
			context.font = `${customConfig?.y_axis_left?.tick?.font_size}px Open Sans`;

			let truncatedText = str;
			while (context.measureText(truncatedText + "...").width > maxWidth && truncatedText.length > 0) {
				truncatedText = truncatedText.slice(0, -1);
			}
			if (truncatedText.length < str.length) {
				showToolTip = true;
			} else {
				showToolTip = false;
			}

			return truncatedText.length < str.length ? truncatedText + "..." : str;
			// }
			// else {
			// 	showToolTip = false;
			// 	return formatValue(payload.value, format);
			// }
		};

		const displayValue = getEllipsisText(value, maxWidth);

		return showToolTip ? (
			<g transform={`translate(${x},${y})`}>
				<foreignObject x={isMini ? 0 : type?.toLowerCase() === chartType.Bar?.toLowerCase() ? -50 : -50} y={-10} width={60} height={20}>
					<TooltipEllips title={payload?.value} overlayClassName="quant-tooltip-reduced-padding">
						<div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
							<span
								style={{
									fontSize: customConfig?.y_axis_left?.tick?.font_size,
									color: customConfig?.y_axis_left?.tick?.label_color,
									fontWeight: customConfig?.y_axis_left?.tick?.font_weight,
								}}>
								{/* {getEllipsisText(payload?.value, maxWidth)} */}
								{displayValue}
							</span>
						</div>
					</TooltipEllips>
				</foreignObject>
			</g>
		) : (
			<g transform={`translate(${x},${y})`}>
				<foreignObject x={isMini ? 0 : type?.toLowerCase() === chartType.Bar?.toLowerCase() ? -50 : -50} y={-10} width={60} height={20}>
					{/* <TooltipEllips title={payload?.value} overlayClassName="quant-tooltip-reduced-padding"> */}
					<div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
						<span
							style={{
								fontSize: customConfig?.y_axis_left?.tick?.font_size,
								color: customConfig?.y_axis_left?.tick?.label_color,
								fontWeight: customConfig?.y_axis_left?.tick?.font_weight,
							}}>
							{/* {getEllipsisText(payload?.value, maxWidth)} */}
							{displayValue}
						</span>
					</div>
					{/* </TooltipEllips> */}
				</foreignObject>
			</g>
		);
	};

	// Custom label component to show only first and last labels
	const CustomLabel = useMemo(() => {
		return (props) => {
			const { x, y, value, index, dataKey } = props;

			const isFirstOrLast = index === 0 || index === data.length - 1;
			if (!isFirstOrLast) return null;

			const format = obj?.graph_customization?.dlabel_fmt || "##";
			const valueFormated = formatValue(Number(value), format);

			return (
				<text
					x={index === 0 ? x - 20 : index === data.length - 1 ? x + 20 : x}
					y={y}
					textAnchor={customConfig?.resolved_data_label?.text_anchor}
					fill={obj?.colorLegends?.[dataKey] || customConfig?.resolved_data_label?.label_color}
					fontSize={obj.graph_customization?.dlabel_size ? `${obj.graph_customization.dlabel_size}px` : `${customConfig?.resolved_data_label?.font_size}px`}
					fontWeight={customConfig?.resolved_data_label?.font_weight}
					onMouseMove={() => setHoveredLineKey(dataKey)}>
					{valueFormated}
				</text>
			);
		};
	}, [data, obj, setHoveredLineKey]);

	const chartMargins = useMemo(() => {
		return {
			left: customConfig?.margin?.left,
			right: customConfig?.margin?.right,
			top: customConfig?.margin?.top,
			bottom: getBottomPosition(),
		};
	}, [isMini, getBottomPosition, renderedFrom]);

	return (
		<>
			<div className="flex h-full w-full">
				{obj?.yAxisLables && !isMini && showYAxisLabel && (
					<div className="flex justify-center items-center max-w-4" style={{ paddingBottom: "50px" }}>
						<AxisLabel title={obj?.yAxisLables} type="y" config={customConfig?.y_axis_label} />
					</div>
				)}

				<ResponsiveContainer height={config.height ?? 250}>
					<LineChart width={config.width} height={config.height} layout={config.layout} data={data} margin={chartMargins}>
						<CartesianGrid
							stroke={customConfig?.cartesian_grid?.stroke}
							strokeDasharray={customConfig?.cartesian_grid?.stroke_dasharray}
							horizontal={config?.h_grid?.toLocaleLowerCase() === "yes" ? true : customConfig?.cartesian_grid?.show_h_grid}
							vertical={config?.v_grid?.toLocaleLowerCase() === "yes" ? true : customConfig?.cartesian_grid?.show_v_grid}
						/>

						<XAxis
							padding={{ left: customConfig?.x_axis_top?.padding?.left, right: customConfig?.x_axis_top?.padding?.right }}
							xAxisId="top"
							type={config.XAxisType}
							dataKey={config.dataKeyXAxis}
							//hide={isMini ? true : obj.graph_customization?.x_vis?.toLocaleLowerCase() !== "yes"}
							hide={customConfig?.x_axis_top?.is_hidden}
							orientation={config.xOrientation}
							axisLine={customConfig?.x_axis_top?.show_axis_line}
							tickLine={customConfig?.x_axis_top?.show_tick_line} //CustomXAxisTickBarTop
							tick={<CustomXAxisTickBarTop2 />}
							stroke={customConfig?.x_axis_top?.stroke}
							strokeWidth={customConfig?.x_axis_top?.stroke_width}
						/>

						<XAxis
							tickLine={customConfig?.x_axis_bottom?.show_tick_line}
							padding={{ left: customConfig?.x_axis_bottom?.padding?.left, right: customConfig?.x_axis_bottom?.padding?.right }}
							orientation="bottom"
							domain={[0, "auto"]}
							dataKey={config.dataKeyXAxis}
							hide={isMini || obj?.graph_customization?.x_vis?.toLocaleLowerCase() !== "yes"}
							tick={<CustomXAxisTickBottomLine barCount={data?.length} />}
							stroke={customConfig?.x_axis_bottom?.stroke}
							strokeWidth={customConfig?.x_axis_bottom?.stroke_width}
							height={customConfig?.x_axis_bottom?.height}
							interval={customConfig?.x_axis_bottom?.interval}
						/>

						<YAxis
							dataKey={config.dataKeyYAxis}
							padding={{ bottom: customConfig?.y_axis_left?.padding?.bottom }}
							type={config.YAxisType}
							hide={isMini || !showYAxisLabel || obj?.graph_customization?.y_vis?.toLocaleLowerCase() !== "yes"}
							stroke={customConfig?.y_axis_left?.stroke}
							strokeWidth={customConfig?.y_axis_left?.stroke_width}
							tickLine={customConfig?.y_axis_left?.show_tick_line}
							tick={<CustomYAxisTick />}
							style={{ zIndex: 9000 }}
							domain={[0, maxDomain]}
						/>

						{config.showTooltip && !isMini && (
							<Tooltip
								content={
									<CustomTooltip
										chartDataTooltip={obj?.chartDataTooltip}
										graphCustomization={obj?.graph_customization}
										setIsTooltipHovered={setIsTooltipHovered}
										type={"custom"}
										hoveredBarKey={hoveredLineKey}
										customConfig={customConfig}
									/>
								}
								cursor={{ fill: customConfig?.tooltip?.color }}
							/>
						)}
						{config.showLegends && <Legend />}
						{barKeys.map((key) => {
							return (
								<Line
									key={key}
									type="linear"
									dataKey={key}
									stroke={obj?.colorLegends?.[key]}
									strokeWidth={1}
									activeDot={{ r: chartStyleConfigLine?.activeDotSize }}
									onMouseMove={() => setHoveredLineKey(key)}
									connectNulls={false}
									// onMouseLeave={() => setHoveredLineKey(null)}
									dot={(props) => {
										const value = props.payload[props.dataKey];
										if (value === null || value === undefined || isNaN(Number(value))) {
											return null;
										}
										else return (
											<CustomMarker
												{...props}
												markerType={obj?.graph_customization?.lc_marker_sp?.[key]}
												markerSize={obj?.graph_customization?.line_marker_size?.[key] || 8}
												isMini={isMini}
												onMouseEnter={() => setHoveredLineKey(key)}
												onMouseMove={() => setHoveredLineKey(key)}
											/>
										)
									}}>
									{config?.show_dlabels && !isMini && <LabelList dataKey={key} content={(props) => <CustomLabel {...props} dataKey={key} />} />}
								</Line>
							);
						})}
					</LineChart>
				</ResponsiveContainer>
			</div >
			{obj?.xAxisLables && !isMini && (
				<div className="flex justify-center items-center -mt-8 max-h-4">
					<AxisLabel title={obj?.xAxisLables} type="x" config={customConfig?.x_axis_label} />
				</div>
			)
			}
		</>
	);
};
